import { Box } from "@mui/material";
import AppleIcon from "@mui/icons-material/Apple";
import AndroidIcon from "@mui/icons-material/Android";
import { landingColors } from "../landingColors";

// Native capture resolution per platform (iPhone 17 Pro Max simulator for
// iOS, Medium_Phone_API_35 emulator for Android) — used as the aspect ratio
// so screenshots aren't stretched/cropped into a generic phone shape.
const PLATFORM_ASPECT = {
  ios: "1320 / 2868",
  android: "1080 / 2400",
};

const ASPECT_BY_VARIANT = {
  phone: "9 / 19.5",
  wide: "16 / 9",
  card: "4 / 3",
};

const PLATFORM_ICON = {
  ios: AppleIcon,
  android: AndroidIcon,
};

// Device frame geometry, all as a percentage of the device's own width (see
// the cqw note below for why these aren't plain percentages). Corner radius
// is platform-specific: an iPhone's corners are ~13-14% of its width, and its
// captures reserve a tall Dynamic Island band that keeps status bar content
// clear of that curve. Android captures have a short status bar with the
// clock/icons close to the screen edges, so a rounder frame visibly crowds
// them — Android gets a squarer frame to match.
const BEZEL_THICKNESS = 1.7;
const DEVICE_RADIUS_BY_PLATFORM = {
  ios: 13.5,
  android: 6,
};
const DEFAULT_DEVICE_RADIUS = 12;

// Renders a real app screenshot in a simple device bezel (matching the
// dev-docs/screenshots "with-overlay" reference style), with a small badge
// in the corner naming which platform it was captured on.
//
// By default there's no colored mat behind the device, just a drop shadow
// for elevation. Pass `framed` to add a padded background mat instead (its
// color is `screenshotSurface`); only use this when nothing else around the
// screenshot already supplies a surface color.
const AppScreenshot = ({
  src,
  alt,
  platform,
  variant = "phone",
  aspect,
  framed = false,
  sx,
  ...rest
}) => {
  const PlatformIcon = platform ? PLATFORM_ICON[platform] : null;
  const isPhone = variant === "phone";
  const resolvedAspect =
    aspect || (isPhone && platform ? PLATFORM_ASPECT[platform] : ASPECT_BY_VARIANT[variant]);
  const outerRadius = isPhone ? 5 : 4;
  const innerRadius = isPhone ? 3.5 : 2.5;
  // Screen radius is the device radius minus the bezel thickness, which keeps
  // the two curves concentric — otherwise the screen's corners poke past the
  // bezel, or the bezel visibly thickens at the corners.
  const deviceRadius =
    DEVICE_RADIUS_BY_PLATFORM[platform] ?? DEFAULT_DEVICE_RADIUS;
  const screenRadius = deviceRadius - BEZEL_THICKNESS;

  return (
    <Box
      sx={{
        width: "100%",
        // Percentage border-radius resolves horizontally against width and
        // vertically against height independently, so on a tall, narrow box
        // like a phone it produces a squished ellipse instead of a circular
        // corner. Container query width units (cqw), used below, are always
        // relative to this container's width for both axes regardless of the
        // element's own height, giving a true circular corner. (cqw must be
        // read from an ANCESTOR container, not the element that declares
        // container-type itself, or it resolves against the next container
        // up instead — hence this wrapper exists purely to host the query.)
        containerType: isPhone ? "inline-size" : undefined,
        ...(framed && {
          p: 1.5,
          borderRadius: outerRadius,
          bgcolor: landingColors.screenshotSurface,
          boxShadow: landingColors.cardShadow,
        }),
        ...sx,
      }}
      {...rest}
    >
      <Box
        sx={{
          width: "100%",
          boxSizing: "border-box",
          aspectRatio: resolvedAspect,
          bgcolor: isPhone ? "#0b0b0c" : "transparent",
          p: isPhone ? `${BEZEL_THICKNESS}%` : 0,
          borderRadius: isPhone ? `${deviceRadius}cqw` : innerRadius,
          boxShadow: framed ? "none" : landingColors.cardShadow,
          position: "relative",
        }}
      >
        <Box
          sx={{
            width: "100%",
            height: "100%",
            borderRadius: isPhone ? `${screenRadius}cqw` : innerRadius,
            overflow: "hidden",
            position: "relative",
          }}
        >
          <Box
            component="img"
            src={src}
            alt={alt}
            loading="lazy"
            sx={{
              width: "100%",
              height: "100%",
              objectFit: "cover",
              objectPosition: "top center",
              display: "block",
            }}
          />
          {PlatformIcon && (
            <Box
              sx={{
                position: "absolute",
                top: 10,
                right: 10,
                width: 28,
                height: 28,
                borderRadius: "50%",
                bgcolor: "rgba(255,255,255,0.92)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: landingColors.cardShadow,
              }}
            >
              <PlatformIcon sx={{ fontSize: 16, color: landingColors.textPrimary }} />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default AppScreenshot;
